/**
 * Serialización para el nodo de firma de documentos
 */

import type { Input, NodeData } from "@/workflow_api/types/workflow";
import type {
  DigitalSignatureData,
  DigitalSignatureDataReceived,
  Signer,
  SignersSelectedType,
} from "../types";
import type { UserType } from "@/types/entities/users";

type FilesToSign = { id: string; name: string; source: string };

interface DocInput {
  node_id?: string | null;
  name?: string | null;
  value?: string | null;
}

interface NodeInputDocument {
  id?: string;
  name?: string | null;
  connection?: string | null;
  inputs?: DocInput[];
}

interface GeneratedDocumentsValue {
  documents: NodeInputDocument[];
}

/**
 * Serializa los datos del nodo de firma de documentos al formato específico requerido
 */
export const serializeDigitalSignatureNodeSpecific = (
  formData: DigitalSignatureData,
  displayName: string,
  flowId: string,
  positionX: number = 0,
  positionY: number = 0,
  nodeOrder: number = 0,
) => {
  // Determinar el tipo de firma basado en el nombre
  let signTypeValue = "simple";
  if (formData.signatureTypeId.toLowerCase().includes("certified")) {
    signTypeValue = "certified";
  } else if (formData.signatureTypeId.toLowerCase().includes("automatic")) {
    signTypeValue = "automatic";
  }

  // Convertir firmantes a arrays de IDs
  // Por ahora, todos los firmantes son requeridos por defecto
  // const signersIds = formData.signers.map((signer) => signer.id);
  const signers = formData.signers.filter((s) => s.backup || s.primary);

  const internalRequiredSignBy = signers
    .map((x) => ({ ...x.primary }))
    .map((x) => x.id);

  const internalOptionalSignBy = signers
    .map((x) => ({ ...x.backup }))
    .map((x) => x.id);

  // Crear la estructura de documentos generados con conexiones a nodos
  const generatedDocumentsStructure = {
    name: "generated_documents",
    value: null,
    type: "DOCUMENT",
    documents: {
      inputs: formData.filesToSign.map((file, index) => ({
        name: file.source || `documento_${String(index + 1).padStart(2, "0")}`,
        value: file.id,
        type: "STRING",
        connection: file.id,
      })),
    },
  };

  // Crear los inputs en el formato estándar del workflow
  const signDocumentInputs = [
    { name: "sign_type", value: signTypeValue, type: "STRING" },
    { name: "sign_method", value: "digital", type: "STRING" },
    {
      name: "internal_required_sign_by",
      value: internalRequiredSignBy,
      type: "ARRAY",
    },
    {
      name: "internal_optional_sign_by",
      value: internalOptionalSignBy,
      type: "ARRAY",
    },
    generatedDocumentsStructure,
    { name: "only_roles", value: [], type: "ARRAY" },
    { name: "title", value: formData.title, type: "STRING" },
    {
      name: "description",
      value: formData.description || null,
      type: "STRING",
    },
  ];

  return {
    name: "sign-document-node" as const,
    display_name: displayName,
    flow_id: flowId,
    order: nodeOrder,
    position_x: positionX,
    position_y: positionY,
    ref_name: "sign-document-node" as const,
    type: "SIGN_DOCUMENT" as const,
    inputs: signDocumentInputs as Input,
  };
};

/**
 * Deserializa los datos del nodo de firma de documentos desde el formato específico del backend
 */
export const deserializeDigitalSignatureNodeSpecific = (
  nodeData: NodeData,
): DigitalSignatureDataReceived | null => {
  if (!nodeData.inputs || nodeData.inputs.length === 0) {
    return null;
  }

  const inputsMap = nodeData.inputs.reduce<
    Record<
      string,
      | NodeData["inputs"][number]["value"]
      | {
          documents: Array<{ id: string; inputs: Input }>;
        }
      | SignersSelectedType
      | Signer[]
    >
  >((acc, input) => {
    // Si value existe, úsalo normalmente
    if (input.value !== null && input.value !== undefined) {
      acc[input.name] = input.value;
    } else if (Array.isArray(input.documents) && input.documents.length > 0) {
      // Si no hay value pero sí hay documentos, guárdalos
      acc[input.name] = { documents: input.documents };
    } else {
      // Caso default: guarda el value (aunque sea null)
      acc[input.name] = input.value;
    }

    return acc;
  }, {});

  // Convertir el tipo de firma de vuelta a nombre legible
  let signatureTypeName = "Firma simple";
  switch (inputsMap.sign_type) {
    case "simple":
      signatureTypeName = "Firma simple";
      break;
    case "certified":
      signatureTypeName = "Firma certificada";
      break;
    case "timestamp":
      signatureTypeName = "Firma con timestamp";
      break;
    case "automatic":
      signatureTypeName = "Firma automática";
      break;
    default:
      signatureTypeName = "Firma simple";
  }

  // Procesar documentos generados - manejar tanto el formato antiguo como el nuevo
  let filesToSign: FilesToSign[] = [];

  const hasDocuments = (v: unknown): v is GeneratedDocumentsValue =>
    !!v &&
    typeof v === "object" &&
    "documents" in v &&
    Array.isArray((v as GeneratedDocumentsValue).documents);

  const isStringArray = (v: unknown): v is string[] =>
    Array.isArray(v) && v.every((x) => typeof x === "string");

  // Obtenemos el value del input con cualquiera de las dos keys
  const genDocsValue: unknown =
    (inputsMap as Record<string, unknown>)["generated-documents"] ??
    (inputsMap as Record<string, unknown>)["generated_documents"];

  if (hasDocuments(genDocsValue)) {
    // Formato nuevo con documents[].inputs[]
    const items = genDocsValue.documents.flatMap((doc) => {
      if (Array.isArray(doc.inputs) && doc.inputs.length > 0) {
        return doc.inputs.map((inp) => {
          const id =
            inp.value?.toString() ??
            inp.node_id?.toString() ??
            inp.name?.toString() ??
            doc.id?.toString() ??
            "unknown";

          const name =
            inp.name?.toString() ?? doc.name?.toString() ?? `Documento ${id}`;

          const source =
            inp.value?.toString() ??
            inp.node_id?.toString() ??
            inp.name?.toString() ??
            doc.name?.toString() ??
            id;

          return { id, name, source };
        });
      }

      // Fallback si vienen en connection
      const nodeId =
        doc.connection?.toString().split(" ")[0] ??
        doc.name?.toString() ??
        doc.id ??
        "unknown";

      return [
        {
          id: nodeId,
          name: doc.name?.toString() ?? `Documento ${nodeId}`,
          source: nodeId,
        },
      ];
    });

    // Deduplicar
    const dedup = new Map<string, FilesToSign>();
    for (const item of items) {
      dedup.set(`${item.id}::${item.source}`, item);
    }
    filesToSign = [...dedup.values()];
  } else if (isStringArray(genDocsValue)) {
    // Formato antiguo
    filesToSign = genDocsValue.map((docId) => ({
      id: docId,
      name: `Documento ${docId}`,
      source: docId,
    }));
  } else {
    filesToSign = [];
  }

  // Reconstruir los datos del formulario
  return {
    title: (inputsMap.title || "Firma de Documentos") as string,
    description: inputsMap.description as string, // El backend no guarda descripción por ahora
    signatureTypeId: String(inputsMap.sign_type || "simple"),
    signatureTypeName,
    filesToSign,
    signers: [
      ...((inputsMap.internal_required_sign_by || []) as string[]).map(
        (signer, index) => ({
          primary: signer,
          backup: ((inputsMap.internal_optional_sign_by || []) as string[])[
            index
          ]
            ? ((inputsMap.internal_optional_sign_by || []) as string[])[index]
            : null,
        }),
      ),
    ],
  };
};

// Función para transformar usuarios del API a Signers
export const transformUserToSigner = (user: UserType): Signer => {
  // Construir el nombre completo del usuario
  const fullName =
    `${user.name || ""} ${user.last_name || ""}`.trim() ||
    user.email.split("@")[0];
  return {
    id: user.id,
    name: fullName,
    email: user.email,
    role: user.position?.name || "Sin posición",
    positionId: user.position?.id || "",
  };
};
